// ---------------------------------------------------------------------------
// Numbers play: the step builder.
//
// Two rules here are the whole screen:
//
//   1. Any mix of operands is legal. Tile and tile, result and tile, and result
//      and result: `A x B` is as ordinary as `A x 3`, so results are rendered as
//      selectable values rather than as a history readout.
//   2. The interface never says whether the player is right and never hints at
//      a solution. No verdict, no "close", no highlight when a step happens to
//      land on it. Players read their own working and judge for themselves.
//
//      **AMENDED 2026-08-03, and only this far.** It used to say the distance
//      to the number is never shown either, and that had to go with the rule
//      change under it: the round now scores the best value the player REACHED
//      rather than whatever is on the board when the clock stops (DESIGN §6.4).
//      A player cannot be scored on a thing the screen refuses to tell them, so
//      there is now exactly one line that names a value and how far off it is —
//      `bestReadout`, in the header — and nothing else on the screen changed.
//      The step being built says nothing, the working list says nothing, no
//      tile or operator is ever marked as the good one. The test `states no
//      verdict and no distance anywhere but the one best readout` is what holds
//      the amendment to that width.
//
// Legality comes from the shared validator through useNumbersBuilder, so the
// client blocks exactly what the server would reject, with the same reason.
//
// **The layout is one equation and one pad**, rebuilt after the round was played
// in anger. It used to be four stacked sections — tiles, results, operators,
// working — with the step under construction as a small row at the bottom of the
// last of them, and building `10 x 2 x 3` meant travelling the whole screen
// twice. Now:
//
//   * **The step being built is the biggest thing under the number**, because it
//     is the only thing the player is actually doing. It was a row in the
//     working list, on the argument that a staging area makes "add a step" feel
//     like a trick. The argument was right about the trick and wrong about the
//     size: it is still a step that has to be added, it still wears the letter it
//     is about to get, and `Add step` is now attached to the equation itself
//     rather than to a row three sections down.
//   * **Every tappable value and operator is in one pad**, directly under it.
//     Tiles, results and the four operators, one card, no headings between them.
//     A step is built without the eye leaving a hand's width of screen.
//   * **A committed result is carried into the next step**, and the pad draws it
//     as carried: dashed, not solid. See useNumbersBuilder — while it is carried
//     the next value tapped replaces it, so a second independent branch costs
//     nothing, and the line under the equation says so while that is true.
//
// Three things survived the rebuild unchanged, and each one is easy to undo by
// accident:
//
//   * **A blocked operator is explained on its own button, never as a sentence
//     about the step.** `operatorBlockedReason(op)` answers a hypothetical: what
//     WOULD go wrong if this operator were chosen. Rendering those answers as a
//     list of errors is how a player who built `25 x 7` was told `25 / 7 is not
//     a whole number`, about an operator they never touched. The only sentence
//     this screen shows as an error is `pendingError`, which is about the
//     operator the player actually selected. The hypotheticals live on the
//     disabled buttons, as their description.
//   * **Only a committed step spends its operands.** A pending step claims a
//     tile, it does not spend it, which is what lets a half-built step be cleared
//     for free — and it is why nothing here auto-commits the moment a legal
//     triple exists. Auto-commit would make every mis-tap cost an undo.
//   * **Nothing appears without space already reserved for it.** The equation
//     keeps its message line and its value slot whether or not they have anything
//     in them, and locking swaps only the block at the bottom.
//
//     The sync indicator is the reason the number to make used to slide: it sat
//     in the header beside it and grew a few words whenever a draft was in
//     flight. It now lives in the strip instead, in a row of fixed height, where
//     it has nothing to push. Nothing that can change size goes in that header
//     row again — the number is the biggest thing on the screen and a player
//     reads it while typing.
//
// **AMENDED: on a phone the column above is flipped, and the typed field is on
// phones too.** Everything above this paragraph is still true of the desktop
// layout, which is untouched. What changed is that the round was played on a
// phone and the verdict was that a thumb cannot work it: the pad sat under the
// equation but `Add step` was attached to the equation ABOVE the pad, so
// committing a step was a trip up the screen and back, and the working list,
// the roster and the lock button were spread down a column that scrolled.
//
// **AMENDED AGAIN: the phone's lock is held, not tapped.** Putting `Add step`
// and `Lock in` one above the other under the same thumb, both full width, both
// 56px, both the primary blue, made two buttons that read as one — and the lower
// of the two ends the round. It is a `HoldButton` now: a pill, 48px, in the good
// green, filling left to right for 600ms under the finger. A stray tap starts
// the fill and does nothing else. Desktop is untouched and keeps the plain
// button, because the mis-press this is about is a thumb's, and a keyboard
// reaches the same action with Tab and Enter on either layout.
//
// Below `useIsDesktop()` the screen is two halves and they do not mix:
//
//   * **The top half is read, and only read.** The clock, the number to make and
//     the working list. The header is pinned; the working list and the roster
//     scroll under it, because a round can reach NUMBERS_MAX_STEPS and the list
//     is the one thing here with no ceiling. It is scrolled to its end whenever a
//     step lands, so the newest step — the one wearing `undo` — sits against the
//     touch half rather than off the bottom of the region.
//   * **The bottom half is touch, and everything the thumb needs is in it**, in
//     the order it is used: the equation, the value pad, the four operators, a
//     full-width `Add step` directly under the pad, `Lock in` under that, and the
//     typed field last. Nothing in it scrolls. The read half absorbs every
//     change of height, which is what keeps the number to make where it was.
//
// The pad is a fixed grid on a phone rather than a wrapping row: six tile cells,
// five result cells whether or not there are five results, four operators. A
// value is in the same place in every round, and the row under it does not move
// when a result arrives. That costs four dashed empty cells at the start of a
// round and buys a board that never rearranges itself under a thumb.
//
// Still no auto-commit. It was offered again with the flip and declined again,
// and the argument above is unchanged: a step that commits itself the moment a
// legal triple exists makes every mis-tap cost an undo.
//
// The typed field is DESIGN 6.4's second input surface, and it is an addition
// rather than an alternative: the tap path is unchanged by its presence. It runs
// shared's `parseExpression`, so a typed answer and a tapped one are the same
// step list and the server cannot tell them apart. Because that parser reads a
// whole answer from the tiles and always numbers its steps from A, submitting
// replaces the working list rather than extending it — the one seam between the
// surfaces, and the line under the field states it before the player commits,
// not after. It says `Enter` on a desktop and `Use this` on a phone, because
// those are the two things that actually submit it.
//
// **What the phone field does about the keyboard**, which is the whole reason it
// was desktop-only:
//
//   * `inputMode="numeric"`, not `text`. Digits are most of what is typed here
//     and a text keyboard buries every one of them a page deep; worse, iOS puts
//     `+` and `*` a page deeper still, behind `#+=`. A numeric pad gives the
//     digits at full size and gives up the operators entirely —
//   * — so **the operators come from a strip of our own**, above the field and
//     above the keyboard: `+ - × ÷ ( )`, inserted at the caret. They are the one
//     thing a numeric keypad cannot type and the one thing a text keyboard hides.
//     They insert `*` and `/` while showing `×` and `÷`, so what the field holds
//     is what `stepsToExpression` would have written. They are not the operator
//     pad and they never carry a blocked reason: they edit text, they do not
//     choose an operator.
//   * **While the field holds focus the pad is not rendered at all.** The
//     keyboard takes half the screen, the field is the input surface for as long
//     as it is focused, and a pad squeezed into what is left is a pad with 20px
//     targets. What stays is the number to make, the working list, a one-line
//     read-only copy of the six tiles — the objection to a phone field was always
//     that the keyboard covers the board it is about — and the strip.
//   * Everything in that state is held off the bottom by `bottomInset` from
//     `useVisualViewport`, because iOS does not resize the layout viewport for
//     the keyboard, it scrolls it.
//   * The field is the last child of the touch half in BOTH states, at the same
//     position in the element tree, so focusing it neither moves it nor remounts
//     it. Moving it would fire a blur and the two states would flap.
//
// **The window listener is arbitrated by DOM focus and by one carve-out.** It
// ignores every event whose target is editable, which is the typed field, the
// chat box, and anything else with a caret in it: a round's typing must never
// land in the chat box. The carve-out is '/'. `useKeyboardMode` routes it to
// chat from the bubble phase, and division needs it too, so this listener sits
// in the capture phase and takes '/' only while there is a left operand waiting
// for an operator. With nothing being built there is nothing to divide, and the
// slash is chat's exactly as it was.
//
// The chat panel is the room shell's. This screen renders its own column and
// nothing else.
// ---------------------------------------------------------------------------

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  NUMBERS_MAX_STEPS,
  beatsNumbersBest,
  foldNumbersBest,
  nextStepId,
  numbersBestOfSteps,
} from '@countdown/shared';
import type {
  NumbersBest,
  NumbersOperand,
  NumbersOperator,
  NumbersStep,
} from '@countdown/shared';
import { Button } from '../../components/primitives/Button.js';
import { HoldButton } from '../../components/primitives/HoldButton.js';
import { ConnectionStatus } from '../../components/primitives/ConnectionStatus.js';
import { EnterKeyIcon, Icon } from '../../components/primitives/Icon.js';
import { NumberTile } from '../../components/primitives/NumberTile.js';
import { LiveClock } from '../../components/primitives/LiveClock.js';
import { useClock } from '../../hooks/useClock.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useNumbersBuilder } from '../../hooks/useNumbersBuilder.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { roundLabel } from '../../lib/format.js';
import { seatedPlayers } from '../../lib/playerLookup.js';
import { serverNow, useGameStore } from '../../store/gameStore.js';
import { prefersReducedMotion } from '../../store/settingsStore.js';
import { takeNumbersBoardHandoff } from './NumbersDrawingScreen.js';
import type { RoomScreenProps } from './types.js';
import { isEditable } from '../../lib/isEditable.js';

const MONO_LABEL = 'font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-dim';

/**
 * The one line on this screen that says how far from the number the player is.
 *
 * DESIGN §6.4 said the interface never shows the distance to the target, and
 * that rule is now narrowed rather than kept: it holds for the step being built
 * and for every row of the working list, which still say nothing, and it does
 * not hold for THIS, because a round that scores the best value reached has to
 * tell the player what that value is or the rule is a secret. It is a readout
 * of something already decided, not a verdict on what is on the board.
 *
 * Kept short on purpose: it lives in a fixed-width column and a readout that
 * outgrew the column would move the number to make. See the header.
 */
function bestReadout(best: NumbersBest | null): string {
  if (best === null) return 'best —';
  if (best.distance === 0) return `best ${best.value} · exact`;
  return `best ${best.value} · ${best.distance} away`;
}

/**
 * What this player would actually declare right now: their best, which the
 * board is only sometimes.
 *
 * This is "auto submit best" — the board is a scratchpad and undoing back past
 * a good value must not cost it. The board wins every tie, so an ordinary
 * player who never wandered submits exactly the step list in front of them,
 * byte for byte.
 *
 * The engine banks the best independently of anything decided here and scores
 * from its own copy, so this is not the guard — it is what puts a value the
 * SERVER never saw, one built and then undone before any lock or draft carried
 * it, in front of the server at all.
 */
function answerSteps(
  best: NumbersBest | null,
  tiles: number[],
  steps: NumbersStep[],
  target: number | null,
): NumbersStep[] {
  if (best === null || target === null) return steps;
  return beatsNumbersBest(numbersBestOfSteps(tiles, steps, target), best) ? best.steps : steps;
}

const OPERATORS: readonly NumbersOperator[] = ['+', '-', '*', '/'];

/**
 * The UI renders multiplication as a cross. The wire operator stays '*': the
 * server validates '*' and '/', and a '×' would be rejected as unreadable.
 */
const OPERATOR_SYMBOL: Readonly<Record<NumbersOperator, string>> = {
  '+': '+',
  '-': '-',
  '*': '×',
  '/': '/',
};

const OPERATOR_NAME: Readonly<Record<NumbersOperator, string>> = {
  '+': 'add',
  '-': 'subtract',
  '*': 'multiply',
  '/': 'divide',
};

/**
 * Printable keys that pick an operator. '/' is not here: it is chat's shortcut
 * until there is something to divide, and it is handled on its own below. 'x'
 * is included because the button says '×' and that is the key people reach for.
 */
const OPERATOR_KEYS: Readonly<Record<string, NumbersOperator>> = {
  '+': '+',
  '-': '-',
  '*': '*',
  x: '*',
  X: '*',
};

/** Stable identity so the builder's tiles do not change every render. */
const NO_TILES: number[] = [];

/**
 * The board travelling from where it was dealt to where it is played.
 *
 * Transform only, and nothing waits for it: every tile is tappable and the
 * keyboard is live on the first frame, so this spends none of the play clock.
 * That matters more here than it did for the letters board, because DESIGN 6.4
 * is explicit that the deal is watched rather than spent — a hold at the end of
 * it would charge the room for the animation after taking care not to charge
 * them for the deal.
 *
 * Longer than the letters round's 260ms because the journey is longer. There the
 * two rows sit in nearly the same place and the move is a nudge; here the tiles
 * cross most of the screen and, on a phone, cross it in the opposite direction
 * from the target. At 260 that reads as a jump rather than as the board moving.
 */
const ENTER_MS = 320;

/** How stale a capture may be before it is a lie about this device. */
const HANDOFF_MAX_AGE_MS = 2_000;

/**
 * Slide one element out of the box it used to occupy.
 *
 * A plain FLIP: translate and scale from the recorded rect back to `none`, about
 * the top left so the two corrections do not fight. Everything is already laid
 * out where it belongs, so nothing here can reflow the pad under a thumb, and an
 * environment with no `animate` simply shows the finished position.
 *
 * No `will-change` and no fill mode, deliberately. These are tile-shaped
 * elements, and a hint that promotes a layer the animation then tears down is
 * what made the tiles' inset relief blink for a frame at the end of a deal —
 * see the `.tile-flip-in` notes in `index.css`. A 2D transform that ends at
 * `none` is the same matrix the element already has, so there is nothing to
 * switch off.
 */
function slideFrom(element: Element, was: { left: number; top: number; width: number; height: number }): void {
  if (typeof element.animate !== 'function') return;
  const now = element.getBoundingClientRect();
  if (was.width <= 0 || was.height <= 0 || now.width <= 0 || now.height <= 0) return;

  const dx = was.left - now.left;
  const dy = was.top - now.top;
  const sx = was.width / now.width;
  const sy = was.height / now.height;
  const moved = Math.abs(dx) >= 1 || Math.abs(dy) >= 1;
  const resized = Math.abs(sx - 1) >= 0.01 || Math.abs(sy - 1) >= 0.01;
  if (!moved && !resized) return;

  element.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, transformOrigin: 'top left' },
      { transform: 'none', transformOrigin: 'top left' },
    ],
    { duration: ENTER_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
  );
}

function operandLabel(operand: NumbersOperand | null, tiles: number[]): string {
  if (operand === null) return '?';
  if (operand.kind === 'step') return operand.stepId;
  const value = tiles[operand.tileIndex];
  return value === undefined ? '?' : String(value);
}

function isSameOperand(a: NumbersOperand | null, b: NumbersOperand): boolean {
  if (a === null) return false;
  if (a.kind === 'tile' && b.kind === 'tile') return a.tileIndex === b.tileIndex;
  if (a.kind === 'step' && b.kind === 'step') return a.stepId === b.stepId;
  return false;
}

/** The same test `useKeyboardMode` makes before it hands '/' to chat. */

function reasonId(op: NumbersOperator): string {
  return `numbers-op-reason-${OPERATOR_NAME[op]}`;
}

const EXPRESSION_HELP_ID = 'numbers-expression-help';

/**
 * What the strip above the phone keyboard writes into the field.
 *
 * `text` is what lands in the field and `glyph` is what the button shows: the
 * player sees `×` and the field holds `*`, which is what `stepsToExpression`
 * writes and therefore what the field already shows when it is mirroring the
 * working list. Spaces around the operators for the same reason — the canonical
 * form is `75 * 50`, and a field that agrees with itself is one the player can
 * edit rather than re-type.
 *
 * These are not the operator pad. They insert characters; they choose nothing,
 * they validate nothing, and they never carry a blocked reason.
 */
const EXPRESSION_INSERTS: readonly { glyph: string; text: string; label: string }[] = [
  { glyph: '+', text: ' + ', label: 'insert a plus' },
  { glyph: '-', text: ' - ', label: 'insert a minus' },
  { glyph: '×', text: ' * ', label: 'insert a times' },
  { glyph: '÷', text: ' / ', label: 'insert a divide' },
  { glyph: '(', text: '(', label: 'insert an opening bracket' },
  { glyph: ')', text: ')', label: 'insert a closing bracket' },
];

/**
 * One operand of the equation being built.
 *
 * `carried` is the whole point of the component: a value the chain put there is
 * drawn dashed and in the accent-blue the pending letter uses, a value the
 * player chose is drawn solid and in ink. If those two ever look the same, the
 * fact that tapping replaces one and not the other becomes invisible.
 */
function OperandSlot({
  label,
  tone,
  describedAs,
  compact = false,
}: {
  label: string;
  tone: 'empty' | 'chosen' | 'carried';
  describedAs?: string;
  /** The phone's equation, which shares its row with the button that clears it. */
  compact?: boolean;
}) {
  return (
    <span
      title={describedAs}
      className={[
        'grid place-items-center rounded-card border px-2 font-mono font-bold tabular',
        compact
          ? 'h-12 min-w-[50px] text-[22px]'
          : 'h-[52px] min-w-[56px] text-[26px] md:h-[62px] md:min-w-[84px] md:text-[32px]',
        tone === 'chosen'
          ? 'border-line-strong bg-raised text-ink'
          : tone === 'carried'
            ? 'border-dashed border-blue-400 bg-raised text-blue-200'
            : 'border-dashed border-line bg-surface-2 text-ink-dim',
      ].join(' ')}
    >
      {label}
      {describedAs === undefined ? null : <span className="sr-only">{describedAs}</span>}
    </span>
  );
}

export function NumbersPlayScreen({ state, send, connection }: RoomScreenProps) {
  const round = state.rounds[state.currentRoundIndex] ?? null;
  const tiles = round?.numberTiles ?? NO_TILES;
  const target = round?.target ?? null;

  const isDesktop = useIsDesktop();
  // What is left of the screen once whatever is covering it is taken off. Only
  // the phone layout uses it, and only to hold the touch half above a keyboard
  // that iOS does not resize the layout viewport for.
  const viewport = useVisualViewport();
  const quality = useGameStore((store) => store.quality);
  const clock = useClock(round?.clock ?? null, { frozen: state.serverPaused });

  // The best value this player has reached this round.
  //
  // Held in a ref as well as in state because the draft below is streamed from
  // inside the builder's own commit handler, before React has re-rendered with
  // the value that commit just produced — a ref is the only copy that is
  // current at that moment. The state copy is what the header reads.
  const bestRef = useRef<NumbersBest | null>(null);
  const bestRoundRef = useRef<number | null>(null);
  const [best, setBest] = useState<NumbersBest | null>(null);

  const streamSteps = connection.streamSteps;
  const builder = useNumbersBuilder(tiles, (next) => {
    // **The draft carries the best, not the board.** A draft is what the server
    // adopts for a player who never locked, so streaming the wreck is how
    // pressing `Start over` on a finished 175 used to end the round with
    // nothing. Not wrapped in a callback: `useNumbersBuilder` keeps `onChange`
    // in a ref it refreshes every render, so a fresh identity costs nothing.
    streamSteps(answerSteps(bestRef.current, tiles, next, target));
  });

  // Optimistic, so a second tap cannot double-lock while the round state is in
  // flight. The server's own record is what the rest of the screen trusts.
  const [lockSent, setLockSent] = useState(false);
  // A lock is the server's to record; choosing to edit again is local. The
  // engine accepts a replacement lock while the clock runs, so this is a real
  // second chance rather than a button that quietly does nothing.
  const [editing, setEditing] = useState(false);
  const ownSubmission = round?.ownSubmission ?? null;
  const locked = (lockSent || ownSubmission?.lockedAt != null) && !editing;
  // The server's copy, not the builder's, so a player who reconnects after
  // locking reads back the value they declared even before their working list
  // has been restored.
  const declaredValue =
    ownSubmission !== null && ownSubmission.kind === 'numbers'
      ? ownSubmission.value
      : builder.value;

  // Restore the working list after a reconnect. `hydrate` refuses once anything
  // is committed locally, so this cannot overwrite live working; without it a
  // player who dropped mid-round comes back unable to read their own steps.
  const { hydrate } = builder;
  useEffect(() => {
    if (ownSubmission?.kind !== 'numbers') return;
    hydrate(ownSubmission.steps);
  }, [ownSubmission, hydrate]);

  // Fold everything the working list has passed through into the best held, and
  // take the server's copy whenever it is ahead — which it is exactly once, on
  // a reconnect, where this device has no history and the round does.
  //
  // Nothing here can lose a value. `foldNumbersBest` only ever replaces the
  // held best with a strictly better one, so undoing a step, starting over and
  // typing a worse expression over the top all leave it where it was. The one
  // thing that clears it is the round changing, which is a different round.
  const serverBest = round?.ownNumbersBest ?? null;
  const committedSteps = builder.steps;
  const bestRoundIndex = round?.index ?? null;
  useEffect(() => {
    if (bestRoundRef.current !== bestRoundIndex) {
      bestRoundRef.current = bestRoundIndex;
      bestRef.current = null;
    }
    let next = bestRef.current;
    if (serverBest !== null && beatsNumbersBest(next, serverBest)) next = serverBest;
    if (target !== null) next = foldNumbersBest(next, tiles, committedSteps, target);
    bestRef.current = next;
    // Identity, not equality: `foldNumbersBest` hands the held best straight
    // back when nothing beat it, so this bails out of a render on every
    // keystroke that did not improve anything.
    setBest((held) => (held === next ? held : next));
  }, [bestRoundIndex, serverBest, committedSteps, tiles, target]);

  /**
   * The answer as it stands, and the value it declares.
   *
   * The board unless the best beats it, which is the same rule the draft above
   * streams by and the same one the lock below sends by — so the number on the
   * lock button is always the number that would be scored.
   */
  const answer = useMemo(() => {
    const board = target === null ? null : numbersBestOfSteps(tiles, committedSteps, target);
    if (best !== null && beatsNumbersBest(board, best)) {
      return { steps: best.steps, value: best.value };
    }
    return { steps: committedSteps, value: board?.value ?? null };
  }, [best, committedSteps, tiles, target]);

  // The typed surface. On both now — see the note at the top about what the
  // phone does with the keyboard, which is the whole of why it was not.
  const [expression, setExpression] = useState('');
  const [expressionError, setExpressionError] = useState<string | null>(null);
  // Focus, held here as well as in the DOM because it chooses a layout. A phone
  // hides the pad while the field is the input surface, and a layout cannot be
  // read off `document.activeElement` during a render.
  const [expressionFocused, setExpressionFocused] = useState(false);
  const expressionField = useRef<HTMLInputElement>(null);
  const canonicalExpression = builder.canonicalExpression;

  // The field mirrors the working list whenever the player is not typing in it,
  // so the two surfaces never show different answers. While it holds focus it
  // is theirs, and nothing rewrites it under them mid-keystroke.
  useEffect(() => {
    if (expressionField.current !== null && document.activeElement === expressionField.current) {
      return;
    }
    setExpression(canonicalExpression ?? '');
    setExpressionError(null);
  }, [canonicalExpression]);

  const { left, op, right, carried } = builder.pending;
  const pendingValue = builder.pendingValue;
  // The letter the step being built is about to be given. Same function the
  // commit uses, over the same committed list, so the row cannot promise a
  // letter the step does not get.
  const pendingId = nextStepId(builder.steps);
  const atStepLimit = builder.steps.length >= NUMBERS_MAX_STEPS;
  const hasPending = left !== null || op !== null || right !== null;

  const { steps, selectOperand, selectOperator, commit, clearPending, undoLast } = builder;

  // The physical keyboard. CLAUDE.md promises one on desktop and this screen had
  // none at all: no digits, no operators, no Enter. Everything here is guarded
  // on the event target being uneditable, so chat and the typed field keep every
  // key pressed inside them.
  useEffect(() => {
    if (locked) return;

    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      if (isEditable(event.target)) return;
      const key = event.key;

      if (key === '/') {
        // Chat's shortcut while there is nothing to divide. Once an operand is
        // waiting it is division, and the event is stopped here — this listener
        // is in the capture phase, `useKeyboardMode` is on the bubble — so the
        // round's keystroke does not also open the chat box.
        if (left === null) return;
        event.preventDefault();
        event.stopPropagation();
        selectOperator('/');
        return;
      }

      const operator = OPERATOR_KEYS[key];
      if (operator !== undefined) {
        event.preventDefault();
        selectOperator(operator);
        return;
      }

      if (key === 'Enter') {
        // A focused button activates itself on Enter. Committing here as well
        // would spend one press twice.
        if (event.target instanceof HTMLElement && event.target.closest('button, a') !== null) {
          return;
        }
        event.preventDefault();
        commit();
        return;
      }

      if (key === 'Backspace') {
        // Backwards through the work: the half-built step first, and only once
        // there is none does it reach the last committed one.
        event.preventDefault();
        if (hasPending) clearPending();
        else undoLast();
        return;
      }

      // Tiles by position, never by value: two tiles showing 5 are different
      // tiles, and the whole data model addresses them by index.
      if (key >= '1' && key <= '9') {
        const index = Number(key) - 1;
        if (index >= tiles.length) return;
        event.preventDefault();
        selectOperand({ kind: 'tile', tileIndex: index });
        return;
      }

      if (key.length === 1) {
        const stepId = key.toUpperCase();
        if (!steps.some((step) => step.id === stepId)) return;
        event.preventDefault();
        selectOperand({ kind: 'step', stepId });
      }
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [
    locked,
    left,
    hasPending,
    steps,
    tiles,
    selectOperand,
    selectOperator,
    commit,
    clearPending,
    undoLast,
  ]);

  // The phone's read half, which scrolls. A committed step is appended to the
  // end of the list, so the region is taken to its end whenever one lands: the
  // newest step is the one carrying `undo`, and it belongs against the touch
  // half rather than below the fold. Nothing here sets state — this is a scroll
  // position, and the list is not re-rendered by moving it.
  const readRegion = useRef<HTMLDivElement>(null);
  const committedCount = steps.length;
  useEffect(() => {
    const region = readRegion.current;
    if (region === null) return;
    region.scrollTop = region.scrollHeight;
  }, [committedCount]);

  // The board arriving from the screen that dealt it.
  //
  // `numbers-drawing` and `numbers-play` never coexist — the phase change swaps
  // one for the other between frames — so the before-frame of this FLIP was
  // measured over there, as the tiles landed and again once the target had
  // settled, and it is taken here.
  //
  // **Nothing here decides a direction, and "slide up" is not what happens.**
  // Each element is animated from wherever it was to wherever it now is, and the
  // layout settles the rest — which is the only version that survives two
  // layouts that disagree. Measured in a browser, on both:
  //
  //   phone    tiles move DOWN ~144px into the pad, target UP ~322px into the
  //            header. They cross. The play layout is inverted for thumb reach
  //            (DESIGN 6.4) — reading on top, everything tappable pinned to the
  //            bottom — so the board and the number go opposite ways.
  //   desktop  tiles move DOWN ~185px, target UP ~316px. They cross there too:
  //            the deal centres the board above the target, and the play screen
  //            puts the target in the header with the pad below the equation.
  //
  // So a literal upward slide would be wrong on both. It is also why the tiles
  // are not assumed to be a row: the deal wraps to 5 + 1 in a narrow enough
  // frame, and the sixth tile then travels sideways as well. Each slot carries
  // its own rect, so that costs nothing.
  //
  // The scales differ too and are not cosmetic. The desktop tile is 118x108 on
  // both screens, so it is a pure translate; the phone's is 48x48 dealt and an
  // elastic `pad` cell at 56 tall when played, so it stretches slightly. The
  // target is 64px dealt against 44 (phone) or 76 (desktop) played.
  //
  // Runs once, on the first commit of the round, and blocks nothing.
  const boardRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLSpanElement | null>(null);
  const entered = useRef<number | null>(null);
  const roundIndex = round?.index;

  useLayoutEffect(() => {
    if (roundIndex === undefined || entered.current === roundIndex) return;
    entered.current = roundIndex;
    // Taken whatever happens next, so a capture this screen decided not to use
    // cannot be picked up by a later one. A reload straight into `numbers-play`
    // finds nothing here and animates nothing, which is the correct answer: the
    // board it would slide out of was never on this device.
    const board = takeNumbersBoardHandoff();
    const row = boardRef.current;
    if (row === null || prefersReducedMotion()) return;

    // A stale capture is worse than none.
    const from =
      board !== null && board.round === roundIndex && Date.now() - board.at < HANDOFF_MAX_AGE_MS
        ? board
        : null;
    if (from === null) return;

    Array.from(row.children).forEach((child, slot) => {
      const was = from.tiles[slot];
      if (was !== undefined) slideFrom(child, was);
    });

    const digits = targetRef.current;
    if (digits !== null && from.target !== null) slideFrom(digits, from.target);
  }, [roundIndex]);

  // The one error sentence this screen shows, and it is about the operator the
  // player selected. A locked answer is not being edited, so it says nothing.
  const pendingError = locked ? null : builder.pendingError;

  // One line, always present, so the equation never grows a sentence and pushes
  // the pad down. The error wins. Nothing here narrates a state the equation
  // already shows — the only instruction left is the one that teaches an
  // affordance the player cannot see: that a carried operand can be replaced.
  let pendingMessage = '';
  if (locked) {
    pendingMessage = '';
  } else if (pendingError !== null) {
    pendingMessage = pendingError;
  } else if (atStepLimit) {
    pendingMessage = `You have used all ${NUMBERS_MAX_STEPS} steps. Undo one to build another.`;
  } else if (carried && op === null) {
    pendingMessage = 'Tap any value to swap it.';
  }

  // Typing an expression replaces the working list, because shared's parser
  // reads a whole answer from the tiles and always numbers its steps from A.
  // The line says which it is going to be before the player commits to it, and
  // it is named after the thing that actually submits it — which is not the same
  // on both surfaces: a numeric keypad has no return key at all, so on a phone
  // the button beside the field is the only way in and the line has to say so.
  //
  // The two surfaces need two sentences rather than one with the name slotted
  // in. `Enter` is a noun and can take the verb — "Enter builds your steps" —
  // and the phone's name for the same thing is the button's own label, which is
  // an imperative: "Use this builds your steps" is not English. So the phone
  // says what pressing it will do, in the same order, to the same length.
  const stepCount = builder.steps.length;
  const plural = stepCount === 1 ? 'step' : 'steps';
  let expressionHint = '';
  if (locked) {
    expressionHint = '';
  } else if (stepCount === 0) {
    expressionHint = isDesktop ? 'Enter builds your steps.' : 'Use this to build your steps.';
  } else if (canonicalExpression === null) {
    expressionHint = isDesktop
      ? `Enter replaces your ${stepCount} steps, which do not fit one sum.`
      : `Use this to replace your ${stepCount} steps, which do not fit one sum.`;
  } else {
    expressionHint = isDesktop
      ? `Enter replaces your ${stepCount} ${plural}.`
      : `Use this to replace your ${stepCount} ${plural}.`;
  }
  const expressionMessage = locked ? '' : (expressionError ?? expressionHint);

  const submitExpression = () => {
    const typed = expression.trim();
    if (typed === '' || locked) return;
    // Nothing changes on a failure, so a typo costs the player their text and
    // never the working they already have.
    const failure = builder.applyExpression(typed);
    setExpressionError(failure);
    // Taken, on a phone: give the field up, which puts the keyboard away and
    // the pad back. A refusal keeps the focus, because the text that was
    // refused is still there to be fixed.
    if (failure === null && !isDesktop) expressionField.current?.blur();
  };

  /**
   * Write into the field at the caret, for the strip that sits over the phone
   * keyboard. The selection is restored after React has written the value, or
   * the caret jumps to the end and a player editing the middle of a sum loses
   * their place.
   */
  const insertIntoExpression = (text: string) => {
    const field = expressionField.current;
    if (field === null || locked) return;
    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;
    const next = `${field.value.slice(0, start)}${text}${field.value.slice(end)}`;
    const caret = start + text.length;
    setExpression(next);
    setExpressionError(null);
    requestAnimationFrame(() => {
      field.setSelectionRange(caret, caret);
    });
  };

  const clockVariant = clock.frozen ? 'frozen' : clock.urgent ? 'alert' : 'normal';
  // Everyone, including you. A list of "the others" made you look up your own
  // state somewhere else on the screen to answer the one question the list is
  // for, which is who the round is still waiting on.
  const roster = seatedPlayers(state);

  const lockIn = () => {
    if (locked || answer.value === null) return;
    setLockSent(true);
    setEditing(false);
    send({
      type: 'lock-numbers',
      // The best, when the board is not it. The button said which number this
      // was going to be before it was pressed.
      steps: answer.steps,
      actionId: crypto.randomUUID(),
      // serverNow(), never Date.now(): the device clock may be minutes out and
      // the server checks this reading against the heartbeat.
      clientLockedAt: serverNow(),
    });
  };

  if (round === null || round.type !== 'numbers' || target === null) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 p-4 md:p-6">
        <span className={MONO_LABEL}>numbers</span>
        <p className="text-ink-muted">The board has not landed yet.</p>
      </div>
    );
  }


  const dimmed = locked ? ' opacity-60' : '';
  /** Everything below `useIsDesktop()`. The desktop layout is untouched by it. */
  const compact = !isDesktop;
  /**
   * The typed field is the input surface right now, so on a phone the pad is
   * not on the screen. Focus is the whole test: a keyboard is up whenever the
   * field has the caret, and `keyboardLikely` is a reading rather than a fact.
   */
  const typing = compact && expressionFocused;

  // --- The pieces -----------------------------------------------------------
  // Built once and arranged twice. Two layouts sharing one set of elements is
  // what stops the phone and the desktop drifting into two screens with two
  // sets of rules, which is how the operator reasons got loose the first time.

  const header = (
    <header className="flex shrink-0 items-center gap-3">
      {/* **The best readout costs no height and no thumb room, and that is the
          whole reason it is here.** The clock sets this row's height — 56px on a
          phone against a 26px chip — so the chip has spare space under it that
          nothing has ever used, and the header is the one block on the phone
          layout that is pinned: the read half scrolls itself to the bottom
          whenever a step lands, so a readout in there would be gone by the third
          step, and the touch half is budgeted to the pixel.

          The column is a FIXED width, which is the rule this row already had:
          nothing of variable size may sit left of the number, or the number
          moves while it is being read. So both lines truncate rather than push,
          and the readout is written short enough not to. */}
      <div className="flex w-[140px] shrink-0 flex-col gap-1 md:w-[176px]">
        <span className="truncate self-start rounded-[5px] bg-raised px-2 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">
          {`${roundLabel(round.type).toLowerCase()} ${round.index + 1} of ${state.totalRounds}`}
        </span>
        {/* Announced when it changes, because reaching a new best is the one
            thing on this screen a player wants to be told about without looking
            away from the pad.

            Tighter tracking than the labels beside it, and truncating rather
            than wrapping. This is data rather than a heading, and the numbers
            in it are unbounded — a first step of 75 x 50 on a target of 750
            reads `best 3750 · 3000 away`, which at the label's own 0.12em does
            not fit any phone. Measured at 0.04em everything up to four figures
            of distance does. The value is written FIRST for the same reason: if
            anything is ever clipped it is the end of the distance, and never
            the number the player made. */}
        <span
          data-numbers-best
          aria-live="polite"
          className="h-[14px] truncate font-mono text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-ink-dim"
        >
          {bestReadout(best)}
        </span>
      </div>
      {/* Nothing of variable width belongs between the chip and the number. */}
      <div className="flex items-baseline gap-2 md:gap-3.5">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted md:text-xs">
          make
        </span>
        {/* The number that arrived on the last screen, in the place it lives
            for the rest of the round. It is measured over there and slid into
            here; nothing else in the header moves. */}
        <span
          ref={targetRef}
          className="tabular font-mono text-[44px] font-extrabold leading-none tracking-tight text-ink md:text-[76px]"
        >
          {target}
        </span>
      </div>
      <div className="ml-auto">
        {/* The disc subscribes to the clock itself, so the frame loop that
            moves the wedge stays inside it. `clock` above is the same
            arithmetic at four times a second, for the label and the variant. */}
        <LiveClock
          clock={round?.clock ?? null}
          frozen={state.serverPaused}
          size={isDesktop ? 64 : 56}
          variant={clockVariant}
          label={`${clock.seconds} seconds left`}
        />
      </div>
    </header>
  );

  // The step under construction, and the largest thing on the screen after
  // the number itself. It keeps `data-step-row="pending"`: it is still a
  // step waiting to be added, wearing the letter it is about to get.
  const equation = (
    <section
      data-step-row="pending"
      aria-label="the step you are building"
      className={[
        'flex flex-col rounded-frame border transition-colors duration-150',
        compact ? 'gap-1.5 px-2 py-2' : 'gap-3 px-2 py-3 md:px-5 md:py-4',
        pendingError === null ? 'border-blue-500 bg-blue-500/10' : 'border-alert/60 bg-alert/10',
        locked ? 'opacity-60' : '',
      ].join(' ')}
    >
      <span className="sr-only">{`step ${pendingId}, not added yet`}</span>
      {/* Desktop wraps rather than shrinks: a four-digit value drops to a line
          of its own instead of squeezing the operands. The phone cannot afford
          a second line — the touch half is budgeted to the pixel — so it runs
          smaller and keeps `Clear step` in the same row, right of the value. */}
      <div
        className={
          compact
            ? 'flex items-center gap-1.5'
            : 'flex flex-wrap items-center gap-1.5 md:gap-3'
        }
      >
        <span
          className={[
            'grid shrink-0 place-items-center rounded-chip border border-dashed border-blue-400 font-mono font-bold text-blue-200',
            compact ? 'size-[22px] text-[12px]' : 'size-[26px] text-[13px] md:size-[30px] md:text-[15px]',
          ].join(' ')}
        >
          {pendingId}
        </span>
        <OperandSlot
          compact={compact}
          label={operandLabel(left, tiles)}
          tone={left === null ? 'empty' : carried ? 'carried' : 'chosen'}
          describedAs={
            carried && left !== null
              ? 'carried from your last step, tap any value to replace it'
              : undefined
          }
        />{' '}
        <span
          className={[
            'grid place-items-center font-mono font-bold text-ink',
            compact ? 'h-12 w-6 text-[22px]' : 'h-[52px] w-8 text-[26px] md:h-[62px] md:w-11 md:text-[32px]',
          ].join(' ')}
        >
          {op === null ? <span className="text-ink-dim">?</span> : OPERATOR_SYMBOL[op]}
        </span>{' '}
        <OperandSlot
          compact={compact}
          label={operandLabel(right, tiles)}
          tone={right === null ? 'empty' : 'chosen'}
        />{' '}
        {/* The equals and the value keep their space whether or not the step
            resolves yet, so the equation does not twitch as it is built. */}
        <span
          aria-hidden="true"
          className={`font-mono font-bold text-ink-dim ${compact ? 'text-[22px]' : 'text-[26px] md:text-[32px]'} ${
            pendingValue === null ? 'invisible' : ''
          }`}
        >
          =
        </span>{' '}
        <span
          className={[
            'tabular font-mono font-extrabold text-ink',
            compact ? 'min-w-[46px] text-[24px]' : 'min-w-[56px] text-[30px] md:text-[38px]',
          ].join(' ')}
        >
          {pendingValue === null ? '' : pendingValue}
        </span>
        {compact ? (
          <button
            type="button"
            aria-label="Clear step"
            disabled={!hasPending || locked}
            onClick={clearPending}
            className="ml-auto grid h-11 w-11 shrink-0 place-items-center rounded-card border border-line font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted transition-colors duration-150 disabled:opacity-40"
          >
            clear
          </button>
        ) : null}
      </div>

      <p
        aria-live="polite"
        className={`min-h-[18px] text-[13px] leading-[18px] ${
          pendingError === null ? 'text-ink-dim' : 'font-semibold text-alert'
        }`}
      >
        {pendingMessage}
      </p>

      {/* Desktop keeps its two buttons attached to the equation. A phone puts
          `Add step` full width under the pad instead — the pad is what the
          thumb is on, and a commit that sends it back up the screen is the
          trip this layout exists to remove. */}
      {compact ? null : (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="lg"
            className="flex-1 md:flex-none md:min-w-[190px]"
            disabled={pendingValue === null || locked}
            onClick={commit}
          >
            Add step
            {isDesktop ? <Icon icon={EnterKeyIcon} size={15} /> : null}
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="flex-1 md:flex-none md:min-w-[150px]"
            disabled={!hasPending || locked}
            onClick={clearPending}
          >
            Clear step
          </Button>
        </div>
      )}
    </section>
  );

  const tileButtons = tiles.map((value, index) => {
    const operand: NumbersOperand = { kind: 'tile', tileIndex: index };
    const spent = builder.isConsumed(operand);
    const selected = isSameOperand(left, operand) || isSameOperand(right, operand);
    return (
      <NumberTile
        key={index}
        value={value}
        state={spent ? 'used' : selected ? 'selected' : 'available'}
        // `pad` fills its grid cell rather than carrying a width of its own, so
        // the six of them are one row at any phone width and each one is as big
        // as the row allows.
        size={isDesktop ? 'desktop' : 'pad'}
        // No handler at all when the tile is spent or the answer is locked, so
        // there is nothing to tap rather than a tap that quietly does nothing.
        onClick={spent || locked ? undefined : () => selectOperand(operand)}
      />
    );
  });

  const renderResult = (step: NumbersStep) => {
    const operand: NumbersOperand = { kind: 'step', stepId: step.id };
    const spent = builder.isConsumed(operand);
    const isCarried = carried && isSameOperand(left, operand);
    const selected = isSameOperand(left, operand) || isSameOperand(right, operand);
    return (
      <button
        key={step.id}
        type="button"
        aria-label={`result ${step.id}, ${step.result}`}
        aria-pressed={selected}
        disabled={spent || locked}
        onClick={() => selectOperand(operand)}
        // **A picked result changes COLOUR FAMILY, and it used to change shade.**
        // Selected was `border-blue-400 bg-blue-500/35` against an available
        // `border-blue-500 bg-blue-500/20` — one step of border lightness and
        // fifteen points of the same wash, under a thumb that is covering the
        // tile it just pressed. Isaac reported it as not being able to tell
        // which of A or B he had.
        //
        // So it borrows the numbers TILE's answer, which was rewritten for this
        // exact complaint: everything except a solid accent fill. Accent border,
        // the `accent/15` wash the palette test already solves, an accent ring
        // outside the border, an accent digit, and a one-pixel lift. Blue and
        // orange are not two shades of anything, and the four signals agree.
        //
        // The ring is `shadow` rather than a wider border so the button does not
        // change size as it is picked — the results sit in a grid and a growing
        // cell re-lays the row mid-tap. `transition-colors` grows to carry the
        // shadow and the transform for the same reason.
        className={[
          // Named properties, not `colors`: `transition-property` takes CSS
          // property names, and `colors` is Tailwind's own shorthand rather
          // than one of them.
          //
          // `translate` and not `transform`, which is what this said first and
          // which animated nothing. Tailwind v4's `-translate-y-px` sets the
          // standalone `translate` property; `transform` stays `none`, so the
          // lift was correct and instant. `NumberTile` next door genuinely does
          // use `transform`, because it writes the value itself as an inline
          // style — the two disagree for a reason.
          'fade-in rounded-card border transition-[color,background-color,border-color,box-shadow,translate] duration-150',
          compact
            ? 'flex h-12 min-w-0 flex-col items-center justify-center gap-0.5 px-1'
            : 'flex min-h-11 items-center gap-2 px-3 py-2',
          spent
            ? 'border-line bg-surface-2 opacity-50'
            : isCarried
              ? // A carried left is a suggestion the next tap replaces, so it is
                // the selected treatment with a dashed edge — the same "this is
                // not settled" mark the pending step's own line uses.
                'border-dashed border-accent bg-accent/15 shadow-[0_0_0_3px_var(--c-accent)] -translate-y-px'
              : selected
                ? 'border-accent bg-accent/15 shadow-[0_0_0_3px_var(--c-accent)] -translate-y-px'
                : 'border-blue-500 bg-blue-500/20',
        ].join(' ')}
      >
        {compact ? (
          <>
            <span
              className={`font-mono text-[10px] font-bold leading-none ${
                selected ? 'text-accent' : 'text-blue-200'
              }`}
            >
              {step.id}
            </span>
            <span
              className={`tabular w-full truncate text-center font-mono text-[18px] font-bold leading-none ${
                selected ? 'text-accent' : 'text-ink'
              }`}
            >
              {step.result}
            </span>
          </>
        ) : (
          <>
            {/* The badge keeps a fill, so it keeps `ink-on-fill`. `accent` is a
                light orange in one theme and a dark one in the other, so it may
                not be a fill behind text at all — which is why the selected
                signal is everything around this chip rather than the chip. */}
            <span
              className={`grid size-6 place-items-center rounded-chip font-mono text-xs font-bold text-ink-on-fill ${
                selected ? 'bg-line-strong' : 'bg-blue-500'
              }`}
            >
              {step.id}
            </span>
            <span
              className={`tabular font-mono text-xl font-bold md:text-2xl ${
                selected ? 'text-accent' : 'text-ink'
              }`}
            >
              {step.result}
            </span>
          </>
        )}
      </button>
    );
  };

  const operatorButtons = OPERATORS.map((candidate) => {
    // Why this operator would be illegal for the operands in hand. It belongs
    // to the button and nowhere else: it is a hypothetical about an operator
    // the player has not chosen, so it is never shown as a sentence about their
    // step.
    const blockedReason = builder.operatorBlockedReason(candidate);
    const chosen = op === candidate;
    const unavailable = blockedReason !== null || left === null || locked;
    // The chosen operator's problem is already stated once, on the step itself.
    // Describing it here as well says it twice.
    const describedReason = chosen ? null : blockedReason;
    return (
      <button
        key={candidate}
        type="button"
        aria-label={OPERATOR_NAME[candidate]}
        aria-pressed={chosen}
        aria-describedby={describedReason === null ? undefined : reasonId(candidate)}
        title={describedReason ?? undefined}
        disabled={unavailable}
        onClick={() => selectOperator(candidate)}
        className={[
          'grid place-items-center rounded-card font-mono font-bold transition-colors duration-150',
          compact
            ? 'h-14 w-full text-[26px]'
            : 'h-14 flex-1 text-2xl md:h-[66px] md:w-[150px] md:flex-none md:text-[28px]',
          chosen
            ? 'btn-primary-surface'
            : unavailable
              ? 'border border-line bg-surface-2 text-ink-dim'
              : 'border border-line-strong bg-raised text-ink',
        ].join(' ')}
      >
        {OPERATOR_SYMBOL[candidate]}
        {describedReason === null ? null : (
          <span
            id={reasonId(candidate)}
            data-operator-reason={OPERATOR_NAME[candidate]}
            className="sr-only"
          >
            {blockedReason}
          </span>
        )}
      </button>
    );
  });

  // The pad. Everything tappable is in here and nowhere else: six tiles, the
  // results they have produced, and the four operators.
  const desktopPad = (
    <section
      aria-label="values and operators"
      className={`flex flex-col gap-2.5 rounded-frame border border-line bg-surface p-2 transition-opacity duration-200 md:gap-3 md:p-4${dimmed}`}
    >
      <div
        ref={boardRef}
        data-board
        role="group"
        aria-label="tiles"
        className="flex flex-wrap justify-center gap-1 md:justify-start md:gap-2.5"
      >
        {tileButtons}
      </div>

      {/* Its own row, at a height that is there before the first step is, so
          the operators never jump out from under a thumb mid-round. */}
      <div role="group" aria-label="results" className="flex min-h-11 flex-wrap items-center gap-2">
        <span className={MONO_LABEL}>results</span>
        {builder.steps.map(renderResult)}
      </div>

      <div role="group" aria-label="operators" className="flex gap-1.5 md:gap-2.5">
        {operatorButtons}
      </div>

      {/* Desktop only, because it is about keys. One line, and it is the only
          place the bindings are written down. */}
      {isDesktop ? (
        <p className={`${MONO_LABEL} leading-[14px]`}>
          keys 1-6 tiles · a-e results · + - x / operators · enter adds · backspace clears
        </p>
      ) : null}
    </section>
  );

  /**
   * The same pad as a fixed grid.
   *
   * Six tile cells, five result cells and four operator cells, present in that
   * shape from the first frame of the round to the last. The results row keeps
   * all five whether or not there are five results, so a value that arrives
   * lands in the slot it was always going to have and the operators under it do
   * not move. The four dashed cells at the start of a round are the price, and
   * they also happen to be the only honest place the five-step ceiling is drawn.
   */
  const phonePad = (
    <section
      aria-label="values and operators"
      className={`flex flex-col gap-1.5 transition-opacity duration-200${dimmed}`}
    >
      {/* The same ref as the desktop pad's board. The two are never mounted at
          once — `compact` picks one — so one ref is one board. */}
      <div ref={boardRef} data-board role="group" aria-label="tiles" className="grid grid-cols-6 gap-1.5">
        {tileButtons}
      </div>

      <div role="group" aria-label="results" className="grid grid-cols-5 gap-1.5">
        {Array.from({ length: NUMBERS_MAX_STEPS }, (_unused, index) => {
          const step = builder.steps[index];
          return step === undefined ? (
            <span
              key={`slot-${index}`}
              aria-hidden="true"
              className="h-12 rounded-card border border-dashed border-line"
            />
          ) : (
            renderResult(step)
          );
        })}
      </div>

      <div role="group" aria-label="operators" className="grid grid-cols-4 gap-1.5">
        {operatorButtons}
      </div>
    </section>
  );

  /**
   * The board, while the pad is off the screen.
   *
   * The standing objection to a typed field on a phone was that the keyboard
   * covers the tiles the expression is about. This is the answer to it: the six
   * values on one line, spent ones struck through, nothing tappable about it.
   */
  const tileReadout = (
    <div
      data-tile-readout
      role="group"
      aria-label="your tiles"
      className="flex h-11 shrink-0 items-center justify-between gap-1 rounded-card border border-line bg-surface-2 px-3"
    >
      {tiles.map((value, index) => {
        const spent = builder.isConsumed({ kind: 'tile', tileIndex: index });
        return (
          <span
            key={index}
            className={`tabular font-mono text-[17px] font-bold ${
              spent ? 'text-ink-dim line-through' : 'text-ink'
            }`}
          >
            {value}
          </span>
        );
      })}
    </div>
  );

  /**
   * The operators, for the field rather than for the step.
   *
   * A numeric keypad has no operator on it and a text keyboard hides `*` and
   * `/` behind a shift, so these are how an operator is typed at all. They
   * insert at the caret and keep the focus in the field: `onMouseDown` is
   * prevented, or the tap would blur the field, put the pad back and move the
   * button out from under the finger before the click landed.
   */
  const insertStrip = (
    <div role="group" aria-label="type an operator" className="grid grid-cols-6 gap-1.5">
      {EXPRESSION_INSERTS.map((insert) => (
        <button
          key={insert.glyph}
          type="button"
          aria-label={insert.label}
          disabled={locked}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => insertIntoExpression(insert.text)}
          className="grid h-11 place-items-center rounded-card border border-line-strong bg-raised font-mono text-[20px] font-bold text-ink transition-colors duration-150 disabled:opacity-40"
        >
          {insert.glyph}
        </button>
      ))}
    </div>
  );

  const expressionMessageLine = (
    <p
      id={EXPRESSION_HELP_ID}
      aria-live="polite"
      className={
        compact && !typing
          ? 'sr-only'
          : `min-h-[18px] text-[13px] leading-[18px] ${
              expressionError === null ? 'text-ink-dim' : 'font-semibold text-alert'
            }`
      }
    >
      {expressionMessage}
    </p>
  );

  const typedField = (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submitExpression();
      }}
    >
      <input
        ref={expressionField}
        type="text"
        // Numeric on a phone: digits are most of what is typed here, and both
        // the operators and the brackets come from the strip above rather than
        // from two pages into a symbol keyboard. Desktop is unchanged, where
        // the attribute means nothing anyway.
        inputMode={isDesktop ? 'text' : 'numeric'}
        enterKeyHint="go"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-label="type the whole sum"
        aria-describedby={EXPRESSION_HELP_ID}
        aria-invalid={expressionError !== null}
        disabled={locked}
        value={expression}
        placeholder={tiles.length < 2 ? '' : `${tiles[0]} + ${tiles[1]}`}
        onFocus={() => setExpressionFocused(true)}
        onBlur={() => setExpressionFocused(false)}
        onChange={(event) => {
          setExpression(event.target.value);
          // A sentence about text that has since been edited is a sentence
          // about nothing.
          setExpressionError(null);
        }}
        className={[
          // 17px, never smaller: iOS zooms the whole page in on a field under
          // 16 and the player comes back to a board they have to pinch out of.
          'h-11 min-w-0 flex-1 rounded-card border bg-surface-2 px-3 font-mono text-[17px] text-ink transition-colors duration-150 placeholder:text-ink-dim',
          expressionError === null ? 'border-line-strong' : 'border-alert/60',
        ].join(' ')}
      />
      <Button
        type="submit"
        variant="outline"
        size="sm"
        disabled={locked || expression.trim() === ''}
        onMouseDown={compact ? (event) => event.preventDefault() : undefined}
      >
        Use this
      </Button>
    </form>
  );

  // Committed steps, read from the top. Nothing is built here any more, so
  // it is a record rather than a control surface — except for the one undo,
  // which stays on the last step: an earlier one may be an operand of a
  // later one, and removing it out of order would leave working that does
  // not resolve. `Start over` is the way back from anything deeper.
  const workingList =
    builder.steps.length === 0 ? null : (
      <section className="flex shrink-0 flex-col gap-2">
        <span className={MONO_LABEL}>your working</span>
        <ol className="flex flex-col gap-2">
          {builder.steps.map((step, index) => {
            const isLast = index === builder.steps.length - 1;
            return (
              <li
                key={step.id}
                data-step-row="committed"
                className={[
                  'fade-in flex items-center gap-2.5 rounded-card border px-3 py-2.5 transition-colors duration-150 md:gap-3.5 md:px-4',
                  isLast ? 'border-blue-500 bg-blue-500/15' : 'border-line bg-surface-2',
                  locked ? 'opacity-60' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'grid size-[22px] place-items-center rounded-chip font-mono text-[11px] font-bold text-ink-on-fill md:size-[26px] md:text-[13px]',
                    // Both fills carry white text, so both have to stay dark
                    // in both themes. `blue-700` cannot: it is the tint base
                    // and is pale in light mode.
                    isLast ? 'bg-blue-500' : 'bg-line-strong',
                  ].join(' ')}
                >
                  {step.id}
                </span>
                <span className="font-mono text-[17px] font-semibold text-ink md:text-xl">
                  {`${operandLabel(step.left, tiles)} ${OPERATOR_SYMBOL[step.op]} ${operandLabel(step.right, tiles)}`}
                </span>
                <span aria-hidden="true" className="h-0.5 w-4 bg-line-strong md:w-[22px]" />
                <span className="tabular font-mono text-[19px] font-bold text-ink md:text-2xl">
                  {step.result}
                </span>
                <span className="ml-auto flex items-center gap-3">
                  {isLast ? (
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-blue-200">
                      your answer
                    </span>
                  ) : null}
                  {isLast ? (
                    <button
                      type="button"
                      aria-label="undo"
                      disabled={locked}
                      onClick={undoLast}
                      className="min-h-11 rounded-card border border-line px-3 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted transition-colors duration-150 hover:border-line-strong hover:text-ink disabled:opacity-40"
                    >
                      undo
                    </button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    );

  const rosterBlock = (
    <div className="flex shrink-0 flex-col gap-2">
      {/* Fixed height, and the indicator is right-aligned into space that is
          empty either way, so whatever it decides to say it moves nothing. */}
      <div className="flex h-[34px] items-center gap-2">
        <span className={MONO_LABEL}>players</span>
        <div className="ml-auto flex items-center">
          <ConnectionStatus quality={quality} compact />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {roster.map((player) => {
          // Your own row reads the local lock rather than the broadcast one,
          // so unlocking to edit shows immediately instead of after a round
          // trip. Everyone else only has what the server says.
          const playerLocked =
            player.id === state.youId ? locked : round.submissions[player.id]?.locked === true;
          return (
            <div
              key={player.id}
              className={[
                // `min-w-[132px]` is the floor that makes the line WRAP rather
                // than squeeze, the same argument as the letters roster's 92:
                // `flex-1` with no basis fits sixteen seats onto one line at a
                // dozen pixels each. 132 is a name and a status word side by
                // side, so a 390px phone takes two and starts a third.
                'flex min-w-[132px] flex-1 items-center justify-between gap-2 rounded-tile border px-3 py-2 transition-colors duration-150',
                // The unlocked state carries a transparent border rather than
                // no border: a border appearing on lock would move every row
                // below it by two pixels.
                playerLocked ? 'border-good/50 bg-good/15' : 'border-transparent bg-surface-2',
              ].join(' ')}
            >
              {/* Had no truncate at all, so a 24-character name pushed `locked`
                  off its own row. The status is what may not give way — it is
                  two words long at most and it is the thing the row is read
                  for. */}
              <span className="min-w-0 truncate text-xs font-semibold text-ink md:text-sm">
                {player.displayName}
              </span>
              <span
                className={[
                  'shrink-0 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] md:text-[10px]',
                  playerLocked ? 'text-good' : 'text-ink-dim',
                ].join(' ')}
              >
                {playerLocked ? 'locked' : 'working'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Locking changes this block and nothing above it, so the board the
  // player is reading does not jump the moment they commit.
  const lockBlock = (
    <div className="flex flex-col gap-2">
      {locked ? (
        <div className="fade-in flex flex-wrap items-center gap-2 rounded-card border border-good/50 bg-good/15 px-3 py-2.5">
          <p className="text-sm font-semibold text-ink">Locked in. Clock still running for the rest.</p>
          {declaredValue === null ? null : (
            <span className="ml-auto font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-good">
              {`${declaredValue} declared`}
            </span>
          )}
        </div>
      ) : null}

      {/* A grid on a phone, and it has to be: `block` makes the lock button
          `w-full`, every Button carries `shrink-0`, and a flex row of those two
          adds up to more than a phone is wide — which put `Start over` off the
          right edge of the screen entirely. A track does not care what the
          items would rather be. */}
      <div
        className={
          compact ? (locked ? 'grid' : 'grid grid-cols-[1fr_112px] gap-2') : 'flex gap-2'
        }
      >
        {locked ? (
          <Button
            variant="outline"
            size="lg"
            block
            className="h-[56px] md:h-[62px]"
            onClick={() => setEditing(true)}
          >
            Unlock and edit
          </Button>
        ) : (
          <>
            {/* Two different controls for the same action, and which one is on
                screen is decided by which mis-press is possible.

                On a phone `Add step` is directly above this, full width, 56px
                tall and the primary blue — and the round was played with a thumb
                and the two were pressed for each other. So the phone's lock is
                HELD rather than tapped: a pill rather than a rounded rectangle,
                shorter, in the good green rather than the blue, and it fills
                left to right under the finger so the gesture teaches itself. A
                stray tap starts the fill and nothing else.

                Desktop keeps the plain button. There is no stray click of the
                kind a thumb makes, the two controls are not stacked under one
                finger, and a keyboard reaches this one by Tab and Enter like
                every other button on the screen. `HoldButton` answers Enter and
                Space immediately for the same reason, so the phone layout is
                still operable from a keyboard. */}
            {compact ? (
              <HoldButton
                onHold={lockIn}
                disabled={answer.value === null}
                // No `aria-label`: the visible label is the instruction, so
                // overriding it would give a screen reader a different sentence
                // from the one on the screen for no gain.
                // 48px and a pill, against `Add step`'s 56px rounded rectangle:
                // the smaller footprint is the point, and the row is 56 tall
                // either way, so it is centred in it rather than stretched.
                className="h-12 self-center rounded-full border border-good/60 bg-raised px-4 text-[15px] text-ink"
              >
                <span className="tabular">
                  {answer.value === null ? 'Hold to lock' : `Hold to lock ${answer.value}`}
                </span>
              </HoldButton>
            ) : (
              <Button
                variant="primary"
                size="lg"
                block
                className="h-[56px] md:h-[62px]"
                disabled={answer.value === null}
                onClick={lockIn}
              >
                {answer.value === null ? 'Lock in' : `Lock in ${answer.value}`}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              className={
                compact
                  ? 'h-[56px] w-full px-2'
                  : 'h-[56px] w-[130px] md:h-[62px] md:w-[150px]'
              }
              disabled={builder.steps.length === 0 && !hasPending}
              onClick={builder.reset}
            >
              Start over
            </Button>
          </>
        )}
      </div>
    </div>
  );

  // --- The phone --------------------------------------------------------------
  // Read on top, touch underneath, and the two do not swap places for anything.
  //
  // The column is exactly as tall as what it is given and never scrolls itself:
  // the read half in the middle takes whatever is left over and scrolls inside
  // its own box, so a working list five steps long cannot push the pad off the
  // bottom of the screen.
  if (compact) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="shrink-0 px-3 pt-3 pb-2">{header}</div>

        {/* The read half. `min-h-[88px]` is a floor rather than the `min-h-0` a
            flex child usually wants, and it is there for the phones this budget
            does not fit — an SE has about 500px to give and the touch half alone
            wants 463. Squeezed to nothing, the working list is not smaller, it
            is unreachable: a scroll box of zero height scrolls nowhere. With a
            floor the column overflows instead and the room's own scroll takes
            over, which is a worse layout but a usable one. */}
        <div
          ref={readRegion}
          className="flex min-h-[88px] flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3"
        >
          {rosterBlock}
          {workingList}
        </div>

        {/* The touch half. Every child of it is in a fixed slot, and a slot the
            other state does not use holds null rather than closing up — the
            field is the last of them either way, so focusing it neither moves
            it nor remounts it, and a remount would blur it and flap the two
            states against each other.

            `bottomInset` is what holds the whole block above the keyboard. iOS
            does not resize the layout viewport when the keyboard arrives, it
            scrolls it, so there is nothing else that knows.

            It is applied ONLY while the field has focus, which is the only
            moment this screen puts a keyboard up. Trusting it the rest of the
            time means trusting a reading that anything covering the viewport
            can move: mounted in an iframe — which is what the /dev gallery
            does — `innerHeight` and `visualViewport` belong to two different
            documents, the inset reads as several hundred pixels, and the pad
            gets shoved off the bottom of a screen with no keyboard anywhere
            near it. */}
        <div
          data-touch-half
          className="flex shrink-0 flex-col gap-1.5 border-t border-line bg-surface px-3 pt-2"
          style={{ paddingBottom: typing ? 12 + viewport.bottomInset : 12 }}
        >
          {typing ? tileReadout : equation}
          {typing ? null : phonePad}
          {typing ? insertStrip : null}
          {typing ? null : (
            <Button
              variant="primary"
              size="lg"
              block
              className="h-[56px]"
              disabled={pendingValue === null || locked}
              onClick={commit}
            >
              Add step
            </Button>
          )}
          {typing ? null : lockBlock}
          {expressionMessageLine}
          {typedField}
        </div>
      </div>
    );
  }

  // --- The desktop ------------------------------------------------------------
  // Unchanged: one column, the equation and its own two buttons, the pad, the
  // typed field with its label, the working list, the roster, and the lock.
  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 md:p-6">
      {header}
      {equation}
      {desktopPad}

      {/* The second input surface, promised by DESIGN 6.4 and built on shared's
          own parser. It is an addition: every pixel of the tap path above is
          untouched. */}
      <section className={`flex flex-col gap-2 transition-opacity duration-200${dimmed}`}>
        <span className={MONO_LABEL}>or type the whole sum</span>
        {typedField}
        {expressionMessageLine}
      </section>

      {workingList}
      {rosterBlock}
      {lockBlock}
    </div>
  );
}
